import { z } from 'zod';

/**
 * Roles, permission keys and the role → permissions map. Seeded by
 * `supabase/migrations/20260920000001_identity.sql`; `permissions.test.ts` asserts the two agree.
 * The database is the authority (RLS and RPCs); this map only decides what the UI shows.
 */
export const PERMISSIONS = [
  'org.view',
  'org.manage',
  'org.delete',
  'org.transfer',
  'members.manage',
  'workspaces.manage',
  'billing.view',
  'billing.manage',
  'projects.view',
  'projects.create',
  'projects.edit',
  'projects.move',
  'workspace.manage',
  'grants.manage',
  'orders.create',
  'orders.pay',
] as const;
export const permissionSchema = z.enum(PERMISSIONS);
export type Permission = z.infer<typeof permissionSchema>;

export const ORG_ROLES = ['owner', 'admin', 'billing', 'member'] as const;
export const orgRoleSchema = z.enum(ORG_ROLES);
export type OrgRole = z.infer<typeof orgRoleSchema>;

export const WORKSPACE_ROLES = ['manager', 'orderer', 'viewer'] as const;
export const workspaceRoleSchema = z.enum(WORKSPACE_ROLES);
export type WorkspaceRole = z.infer<typeof workspaceRoleSchema>;

const MANAGER: readonly Permission[] = [
  'projects.view',
  'projects.create',
  'projects.edit',
  'projects.move',
  'workspace.manage',
  'grants.manage',
  'orders.create',
  'orders.pay',
];

export const ROLE_PERMISSIONS: {
  readonly org: Readonly<Record<OrgRole, readonly Permission[]>>;
  readonly workspace: Readonly<Record<WorkspaceRole, readonly Permission[]>>;
} = {
  org: {
    owner: PERMISSIONS,
    admin: PERMISSIONS.filter((p) => p !== 'org.delete' && p !== 'org.transfer'),
    billing: ['org.view', 'billing.view', 'billing.manage', 'orders.pay'],
    member: ['org.view'],
  },
  workspace: {
    manager: MANAGER,
    orderer: ['projects.view', 'projects.create', 'projects.edit', 'orders.create', 'orders.pay'],
    viewer: ['projects.view'],
  },
};

/** Permissions an org role carries, in the org and in every one of its workspaces. */
export function permissionsForOrgRole(role: OrgRole): readonly Permission[] {
  return ROLE_PERMISSIONS.org[role];
}

/** Permissions a workspace role carries in the workspace (or every workspace, for an org-wide grant). */
export function permissionsForWorkspaceRole(role: WorkspaceRole): readonly Permission[] {
  return ROLE_PERMISSIONS.workspace[role];
}

/** What the UI may show at org level. Mirrors `has_org_permission`; the database stays the authority. */
export function orgPermissionSet(role: OrgRole | null): ReadonlySet<Permission> {
  return new Set(role ? ROLE_PERMISSIONS.org[role] : []);
}

/**
 * What the UI may show in one workspace. Mirrors `has_workspace_permission`: the org role, plus the caller's
 * grants on that workspace, plus their org-wide grants (`workspace_id` null).
 */
export function workspacePermissionSet(
  role: OrgRole | null,
  grants: readonly { workspace_id: string | null; role: WorkspaceRole }[],
  workspaceId: string,
): ReadonlySet<Permission> {
  const set = new Set<Permission>(role ? ROLE_PERMISSIONS.org[role] : []);
  for (const grant of grants) {
    if (grant.workspace_id === null || grant.workspace_id === workspaceId) {
      for (const permission of ROLE_PERMISSIONS.workspace[grant.role]) set.add(permission);
    }
  }
  return set;
}
